
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { AlertTriangle, X } from 'lucide-react';

interface ReportErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  podcastName: string;
}

export const ReportErrorDialog: React.FC<ReportErrorDialogProps> = ({
  open,
  onOpenChange,
  podcastName
}) => {
  const { t } = useTranslation();
  const [selectedErrors, setSelectedErrors] = useState<string[]>([]);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    toast
  } = useToast();

  const errorTypes = [{
    id: 'audio_bug',
    label: t('audioBug')
  }, {
    id: 'wrong_info',
    label: t('wrongInfo')
  }, {
    id: 'poor_audio_quality',
    label: t('poorAudioQuality')
  }, {
    id: 'wrong_location',
    label: t('wrongLocation')
  }, {
    id: 'wrong_image',
    label: t('wrongImage')
  }, {
    id: 'wrong_description',
    label: t('wrongDescription')
  }];

  const handleErrorToggle = (errorId: string) => {
    setSelectedErrors(prev => prev.includes(errorId) ? prev.filter(id => id !== errorId) : [...prev, errorId]);
  };

  const handleSubmit = async () => {
    if (selectedErrors.length === 0 && !feedback.trim()) {
      toast({
        title: t('errorTitle'),
        description: t('selectErrorType'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const {
        error
      } = await supabase.from('podcasts').update({
        Erreur: selectedErrors.length > 0 ? selectedErrors.join(', ') : null,
        Feedback: feedback.trim() || null
      }).eq('Name', podcastName);

      if (error) throw error;

      toast({
        title: t('thankYouFeedback'),
        description: t('improveExperience')
      });

      // Reset form
      setSelectedErrors([]);
      setFeedback('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting report:', error);
      toast({
        title: t('errorTitle'),
        description: t('errorSendingReport'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-3rem)] max-w-sm rounded-xl bg-background border shadow-xl mobile-dialog">
        {/* Bouton de fermeture */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          className="absolute top-2 left-2 h-6 w-6 p-0 rounded-full hover:bg-muted z-10"
        >
          <X className="h-3 w-3" />
        </Button>

        <DialogHeader className="text-center space-y-2 pb-1 pt-6">
          <div className="mx-auto w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <DialogTitle className="text-base font-semibold">
            {t('reportError')}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {t('reportErrorDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-2">
            <Label className="text-xs font-medium">
              {t('problemTypes')}
            </Label>
            <div className="grid grid-cols-1 gap-1.5 bg-muted/20 rounded-lg p-2">
              {errorTypes.map(errorType => (
                <div key={errorType.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={errorType.id} 
                    checked={selectedErrors.includes(errorType.id)} 
                    onCheckedChange={() => handleErrorToggle(errorType.id)}
                    className="h-3.5 w-3.5"
                  />
                  <Label htmlFor={errorType.id} className="text-xs cursor-pointer flex-1 leading-tight">
                    {errorType.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Textarea 
              value={feedback} 
              onChange={e => setFeedback(e.target.value)} 
              placeholder={t('describeProblem')} 
              className="min-h-[60px] text-base rounded-lg resize-none mobile-textarea" 
            />
          </div>
        </div>

        <DialogFooter className="pt-1">
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="w-full rounded-lg h-9 text-sm"
          >
            {isSubmitting ? t('sending') : t('validate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
